import { useState } from "react";

import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  Icon,
  isNullish,
  Modal,
  Select
} from "@theorgchartdev/storybook";
import { GetLimitFields } from "./ImportFilterLimitFieldsInput";
import { ImportFilterInput } from "./ImportFilterInput";
import { appstr } from "@coreapp/utils/appstr";
import {
  enumObjectTypes,
  kExcludedLimitFieldsEmployeeCharts,
  kExcludedLimitFieldsPositionCharts
} from "@coreapp/constants";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import { IMPORT_FILTERS_TEST_PREFIX } from "../ImportFiltersUtils";

const ImportFilterModalTitle = props => {
  if (isNullish(props.filter)) {
    return <>{appstr("Create import filter")}</>;
  }

  return <>{appstr("Edit import filter")}</>;
};

export const enumFilterTypes = {
  exclude: "exclude",
  include: "include",
  seed: "seed",
  limitfields: "limitfields"
};

const BuildFilterTypeOptions = onClick => {
  return Object.keys(enumFilterTypes).map(key => ({
    value: key,
    label: appstr(key),
    clickHandler: onClick
  }));
};

const DATA_TEST_ID_PREFIX = `${IMPORT_FILTERS_TEST_PREFIX}-import-filters-modal`;

export const GetFilterType = importFilter => {
  if (!isNullish(importFilter["seed"])) {
    return enumFilterTypes.seed;
  }

  if (!isNullish(importFilter["limitfields"])) {
    return enumFilterTypes.limitfields;
  }

  if (importFilter["exclude"] === false) {
    return enumFilterTypes.include;
  }

  return enumFilterTypes.exclude;
};

export const GetFilteredFieldsList = chartConfig => {
  if (!chartConfig["headers"]) return [];

  let headerList = chartConfig["headers"].slice(0);
  const isPosition = IsPositionChart(chartConfig);
  const excludedFields = isPosition
    ? kExcludedLimitFieldsPositionCharts
    : kExcludedLimitFieldsEmployeeCharts;

  if (isNullish(chartConfig["formulas"])) {
    return headerList.sort();
  }

  const staticFormulas = chartConfig["formulas"].reduce((acc, item) => {
    if (isNullish(item.name)) {
      return acc;
    }

    return [...acc, item.name];
  }, []);

  if (staticFormulas.length === 0) {
    return headerList.sort();
  }

  headerList = headerList.filter(item => {
    return staticFormulas.indexOf(item) === -1 && excludedFields.indexOf(item) === -1;
  });

  return headerList.sort();
};

export const GetFilteredTypedFieldsList = chartConfig => {
  if (!chartConfig["headers"])
    return { [enumObjectTypes.position]: [], [enumObjectTypes.holder]: [] };

  let positionFieldsList = [];
  let holderFieldsList = [];

  if (IsPositionChart(chartConfig)) {
    positionFieldsList = chartConfig["positionFields"]?.sort() ?? [];
    holderFieldsList = chartConfig["holderFields"]?.sort() ?? [];
  } else {
    holderFieldsList = chartConfig["headers"]?.sort() ?? [];
  }

  const fields = {
    [enumObjectTypes.position]: positionFieldsList,
    [enumObjectTypes.holder]: holderFieldsList
  };

  if (isNullish(chartConfig["formulas"])) {
    return fields;
  }

  const staticFormulas = chartConfig["formulas"].reduce((acc, item) => {
    if (isNullish(item.name)) {
      return acc;
    }

    return [...acc, item.name];
  }, []);

  fields[enumObjectTypes.position] = positionFieldsList.filter(item => {
    return staticFormulas.indexOf(item) === -1;
  });

  fields[enumObjectTypes.holder] = holderFieldsList.filter(item => {
    return staticFormulas.indexOf(item) === -1;
  });

  return fields;
};

const normalizeImportFilter = filter => {
  if (
    !isNullish(filter?.["dateBracket"]?.["range"]?.["from"]) &&
    !(filter.dateBracket.range.from instanceof Date)
  ) {
    filter.dateBracket.range.from = new Date(filter.dateBracket.range.from);
  }

  if (
    !isNullish(filter?.["dateBracket"]?.["range"]?.["to"]) &&
    !(filter.dateBracket.range.to instanceof Date)
  ) {
    filter.dateBracket.range.to = new Date(filter.dateBracket.range.to);
  }

  return filter;
};

export const ImportFilterModal = props => {
  const [importFilter, setImportFilter] = useState(() => {
    if (!isNullish(props.filter)) {
      return normalizeImportFilter(structuredClone(props.filter));
    }

    return {
      exclude: true,
      includefilteredmgrs: false,
      values: []
    };
  });

  const filterType = GetFilterType(importFilter);

  const handleSave = () => {
    props.onSave(importFilter);
    props.onClose();
  };

  const handleTypeChange = option => {
    if (filterType === option.value) {
      return;
    }

    const previousValues = importFilter["values"];
    const previousContains = importFilter["valContains"];
    const previousField = importFilter["field"];
    const nextImportFilter = {};

    switch (option.value) {
      case enumFilterTypes.seed:
        nextImportFilter["seed"] = [];
        break;
      case enumFilterTypes.limitfields:
        nextImportFilter["limitfields"] = GetLimitFields(importFilter, props.chartConfig);
        break;
      case enumFilterTypes.exclude:
      case enumFilterTypes.include:
        if (previousValues != null) {
          nextImportFilter["values"] = previousValues;
        } else {
          nextImportFilter["values"] = [];
        }
        if (previousField != null) {
          nextImportFilter["field"] = previousField;
        }
        if (previousContains != null) {
          nextImportFilter["valContains"] = previousContains;
        }
        nextImportFilter["exclude"] = option.value === enumFilterTypes.exclude;
        nextImportFilter["includefilteredmgrs"] = false;
        break;
    }

    setImportFilter(nextImportFilter);
  };

  const ValidateSave = () => {
    let disableSave = false;
    switch (filterType) {
      case enumFilterTypes.seed:
        if (
          importFilter.seed === undefined ||
          importFilter.seed.length === 0 ||
          importFilter.field === undefined
        ) {
          disableSave = true;
        }
        break;
      case enumFilterTypes.limitfields:
        if (Array.isArray(importFilter.limitfields)) {
          disableSave = importFilter.limitfields.length === 0;
        } else {
          const holderCount = importFilter.limitfields?.[enumObjectTypes.holder]?.length ?? 0;
          const positionCount = importFilter.limitfields?.[enumObjectTypes.position]?.length ?? 0;
          disableSave = holderCount + positionCount === 0;
        }
        break;
      case enumFilterTypes.exclude:
        if (
          importFilter.field === undefined ||
          (importFilter.values.length === 0 && importFilter.dateBracket === undefined)
        ) {
          disableSave = true;
        }
        break;
      case enumFilterTypes.include:
        if (
          importFilter.field === undefined ||
          (importFilter.values.length === 0 && importFilter.dateBracket === undefined)
        ) {
          disableSave = true;
        }
        break;
    }
    return disableSave;
  };

  return (
    <CentralizedModalContainer show>
      <Modal show id="import-filter-modal" minWidth="medium" maxWidth="large" centerScreen={true}>
        <DialogTitle
          divider={true}
          onClose={props.onClose}
          data-testid={`${DATA_TEST_ID_PREFIX}-title`}
        >
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"filter"} />
            <ImportFilterModalTitle filter={props.filter} />
          </span>
        </DialogTitle>
        <DialogContent>
          <Flex direction="column" gap="8px" padding="6px 0 0 0">
            <Select
              dataTestId="import-filter-type-select"
              inputOnly={false}
              value={filterType}
              label={`${appstr("Filter")} ${appstr("Type")}`}
              options={BuildFilterTypeOptions(handleTypeChange)}
            />
            <ImportFilterInput
              chartConfig={props.chartConfig}
              isNew={props.isNew}
              importFilter={importFilter}
              setImportFilter={setImportFilter}
              dataTestIdPrefix={DATA_TEST_ID_PREFIX}
            />
          </Flex>
        </DialogContent>
        <DialogActions divider={true}>
          <Flex className={"oc-right-justify"} padding={"4px"}>
            <DialogActions.Tertiary
              dataTestId="import-filter-cancel"
              size={"large"}
              onClick={props.onClose}
            >
              {appstr("Cancel")}
            </DialogActions.Tertiary>
            <DialogActions.Primary
              dataTestId="import-filter-save"
              size={"large"}
              onClick={handleSave}
              disabled={ValidateSave()}
            >
              {appstr("Save")}
            </DialogActions.Primary>
          </Flex>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};
